import React, { useState, useEffect, Fragment } from "react";
import { get, orderBy } from "lodash";

import { formatPostTime, formatPeriod } from "@tvg/formatter/dates";

import { arrowForward } from "../../_static/Icons/icons";
import buildColor from "../../_static/ColorPalette";
import {
  List,
  ListItem,
  Time,
  ArrowIcon,
  RaceChange,
  RaceChangeWrapper,
  OldValue,
  NewValue,
  ShowMoreLink
} from "./styled-components";

export const hasJockeyChanges = (changes, runnerId = "") => {
  const jockey = get(changes, "horse.jockey") || [];

  return runnerId
    ? jockey.findIndex(
        (jockeyChanged) => jockeyChanged.runnerId === runnerId
      ) !== -1
    : jockey.length > 0;
};

export const hasSurfaceChanges = (changes, key) =>
  changes && (get(changes, `surface.${key}`) || []).length > 0;

export const getSurfaceChangesKeys = (changes) =>
  ["condition", "course", "distance"].reduce(
    (acc, key) => (hasSurfaceChanges(changes, key) ? [...acc, key] : acc),
    []
  );

export const hasChangesToShow = (changes, runnerId = "") => {
  if (!changes) return false;

  return (
    getSurfaceChangesKeys(changes).length > 0 ||
    hasJockeyChanges(changes, runnerId)
  );
};

const getDistanceArray = (changesArray) =>
  changesArray
    ? changesArray.map((change) => ({
        keyword: "Distance",
        oldValue: `${
          change.oldValue.value
        }${change.oldValue.code.toLowerCase()}`,
        newValue: `${
          change.newValue.value
        }${change.newValue.code.toLowerCase()}`,
        date: change.date
      }))
    : [];

const getArray = (changesArray, keyword) =>
  changesArray
    ? changesArray.map((change) => ({
        keyword,
        oldValue: change.oldValue,
        newValue: change.newValue,
        date: change.date
      }))
    : [];

const RunnerRaceChanges = (props) => {
  const [showMoreChanges, setShowMoreChanges] = useState(false);
  const [changes, setChanges] = useState([]);

  useEffect(() => {
    if (props.changes && hasChangesToShow(props.changes, props.runnerId)) {
      const surface = get(props.changes, "surface");
      const jockey = get(props.changes, "horse.jockey") || [];

      setChanges(
        orderBy(
          [
            ...getArray(get(surface, "course", []), "Course"),
            ...getArray(get(surface, "condition", []), "Condition"),
            ...getDistanceArray(get(surface, "distance", [])),
            ...getArray(
              props.runnerId
                ? jockey.filter(
                    (jockeyChange) => jockeyChange.runnerId === props.runnerId
                  )
                : jockey,
              "Jockey"
            )
          ],
          ["date"],
          ["desc"]
        )
      );
    }
  }, [props.runnerId, JSON.stringify(props.changes)]);

  return changes.length ? (
    <Fragment>
      <List>
        {changes.map(
          (change, index) =>
            (showMoreChanges || index < props.maxChangesShownDefault) && (
              <ListItem
                key={`${change.keyword}-${
                  change.date
                }-${change.newValue.replace(/ /g, "")}`}
                data-qa-label={`race-change-${change.keyword}`}
              >
                <Time>
                  {formatPostTime(change.date)} {formatPeriod(change.date)}
                </Time>
                <RaceChange>{change.keyword}</RaceChange>
                <RaceChangeWrapper>
                  <OldValue>{change.oldValue || "Set to\u00A0"}</OldValue>
                  {change.newValue && change.oldValue && (
                    <ArrowIcon
                      icon={arrowForward}
                      size={16}
                      color={buildColor("grey", "800")}
                    />
                  )}
                  {change.newValue && <NewValue>{change.newValue}</NewValue>}
                </RaceChangeWrapper>
              </ListItem>
            )
        )}
      </List>
      {!showMoreChanges && changes.length > props.maxChangesShownDefault && (
        <ShowMoreLink
          onClick={() => setShowMoreChanges(!showMoreChanges)}
          data-qa-label={`${props.qaLabel}-show-more`}
        >{`+${
          changes.length - props.maxChangesShownDefault
        } one additional change`}</ShowMoreLink>
      )}
    </Fragment>
  ) : null;
};

RunnerRaceChanges.defaultProps = {
  runnerId: "",
  changes: null,
  maxChangesShownDefault: 2,
  qaLabel: "runner-race-changes"
};

export default RunnerRaceChanges;
